import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { useSessao } from '../../auth/contexto'
import { cn } from '../../lib/cn'
import { TEXTOS } from '../../lib/glossario'
import type { PapelContratual } from '../../types/gmud'
import { IconeConta, IconeSair, IconeSeta } from '../Icones'

/**
 * Como o papel do contrato se chama para quem lê.
 *
 * Mora aqui, e não em `glossario.ts`, porque esta é a única tela que o mostra hoje. No dia em que
 * uma segunda tela precisar do mesmo texto, mude-o de casa ANTES de copiar: dois rótulos iguais em
 * arquivos diferentes é exatamente o que a Regra nº 4 existe para impedir.
 */
const ROTULO_PAPEL_CONTRATUAL: Record<PapelContratual, string> = {
  CONTRATANTE: 'Prefeitura (contratante)',
  CONTRATADA: 'Empresa contratada',
}

/** Para onde cada tecla leva dentro do menu aberto, como a WAI-ARIA define para `role="menu"`. */
const MOVIMENTOS: Record<string, (indice: number, total: number) => number> = {
  ArrowDown: (indice, total) => (indice + 1) % total,
  ArrowUp: (indice, total) => (indice - 1 + total) % total,
  Home: () => 0,
  End: (_indice, total) => total - 1,
}

/**
 * Quem está logado e o que dá para fazer com a própria conta.
 *
 * É menu de verdade, não uma caixa que aparece: Esc fecha e devolve o foco ao botão, as setas
 * andam pelos itens e clicar fora fecha. Sem isso, quem navega por teclado abre o menu e fica
 * preso nele — o foco continua no botão e o conteúdo do menu nunca chega.
 *
 * @returns o botão com o nome de quem está logado e o menu dele
 */
export function MenuDoUsuario() {
  const { usuario, sair } = useSessao()
  const [aberto, setAberto] = useState(false)
  const gatilho = useRef<HTMLButtonElement>(null)
  const caixa = useRef<HTMLDivElement>(null)
  const idDoPainel = useId()
  const idDoGatilho = useId()

  const fechar = useCallback((devolverFoco: boolean) => {
    setAberto(false)
    if (devolverFoco) {
      gatilho.current?.focus()
    }
  }, [])

  useEffect(() => {
    if (!aberto) {
      return
    }
    caixa.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    const aoApontar = (evento: PointerEvent) => {
      const alvo = evento.target as Node
      if (!caixa.current?.contains(alvo) && !gatilho.current?.contains(alvo)) {
        setAberto(false)
      }
    }
    document.addEventListener('pointerdown', aoApontar)
    return () => document.removeEventListener('pointerdown', aoApontar)
  }, [aberto])

  function aoTeclar(evento: KeyboardEvent<HTMLDivElement>) {
    if (evento.key === 'Escape' || evento.key === 'Tab') {
      // Os dois devolvem o foco ao botão. No caso do Tab isso é o que faz a tabulação continuar
      // de onde o usuário estava: sem devolver, o painel some, o foco cai no `body` e o próximo
      // Tab recomeça do topo da página.
      fechar(true)
      return
    }
    const mover = MOVIMENTOS[evento.key]
    if (!mover) {
      return
    }
    evento.preventDefault()
    const itens = Array.from(caixa.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])
    const atual = Math.max(
      itens.findIndex((item) => item === document.activeElement),
      0,
    )
    itens[mover(atual, itens.length)]?.focus()
  }

  if (!usuario) {
    return null
  }

  const organizacao = usuario.organizacao?.sigla ?? usuario.email

  return (
    <div className="relative">
      <button
        ref={gatilho}
        id={idDoGatilho}
        type="button"
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-controls={aberto ? idDoPainel : undefined}
        onClick={() => setAberto((valor) => !valor)}
        onKeyDown={(evento) => {
          if (evento.key === 'ArrowDown') {
            setAberto(true)
          }
        }}
        className={cn(
          'flex max-w-[15rem] items-center gap-2 rounded-lg border border-transparent px-2 py-1.5 text-left',
          'hover:border-slate-300 hover:bg-slate-50',
        )}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-niteroi-50 text-niteroi-700">
          <IconeConta className="h-4 w-4" />
        </span>
        <span className="hidden min-w-0 leading-tight sm:block">
          <span className="block truncate text-sm font-medium text-slate-900">{usuario.nome}</span>
          <span className="block truncate text-xs text-slate-500">{organizacao}</span>
        </span>
        <IconeSeta className={cn('h-4 w-4 shrink-0 text-slate-400', aberto ? '-rotate-90' : 'rotate-90')} />
      </button>

      {aberto && (
        <div
          ref={caixa}
          id={idDoPainel}
          onKeyDown={aoTeclar}
          className="absolute right-0 z-40 mt-1 w-72 animate-surgir rounded-xl border border-slate-200 bg-white shadow-cartao-alto"
        >
          <div className="border-b border-slate-100 px-4 py-3">
            <p className="text-sm font-semibold text-slate-900">{usuario.nome}</p>
            <p className="truncate text-xs text-slate-500">{usuario.email}</p>
            {usuario.organizacao && (
              <p className="mt-1 text-xs text-slate-600">
                {usuario.organizacao.nome} ({usuario.organizacao.sigla})
              </p>
            )}
            {usuario.papelContratual && (
              <p className="mt-1 text-xs text-slate-600">{ROTULO_PAPEL_CONTRATUAL[usuario.papelContratual]}</p>
            )}
          </div>
          <div role="menu" aria-labelledby={idDoGatilho} className="py-1">
            <Link
              to="/perfil"
              role="menuitem"
              onClick={() => setAberto(false)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
            >
              <IconeConta className="h-4 w-4 text-slate-400" />
              {TEXTOS.minhaConta}
            </Link>
            <button
              type="button"
              role="menuitem"
              onClick={sair}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900"
            >
              <IconeSair className="h-4 w-4 text-slate-400" />
              {TEXTOS.sair}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
